import proj4 from 'proj4';

export function elementKey(el: {id: number | string, source?: string} | any): string {
    return `${el?.source ?? 'xrf'}_${el?.id}`;
}

// Element names from /minerals and /xrf now carry their unit in square brackets,
// e.g. "Fe [%]". Split the display name from the unit so the name is shown as
// before and the unit can be appended wherever the value is displayed.
export function parseNameAndUnit(raw: string): {name: string, unit: string} {
    if (!raw) return {name: raw, unit: ''};
    const match = raw.match(/^(.*?)\s*\[([^\]]*)\]\s*$/);
    if (match) return {name: match[1].trim(), unit: match[2].trim()};
    return {name: raw.trim(), unit: ''};
}

// Suffix used after a numeric value, e.g. unitSuffix('%') -> ' %'.
export function unitSuffix(unit?: string | null): string {
    return unit ? ` ${unit}` : '';
}

const ELEMENT_COLORS = [
    '#e6194b', '#3cb44b', '#ffe119', '#0082c8', '#911eb4',
    '#f032e6', '#fabebe', '#008080', '#aa6e28', '#800000',
    '#808000', '#000080', '#46f0f0', '#d2f53c', '#f58231',
    '#e6beff', '#aaffc3', '#ffd8b1', '#808080', '#fffac8',
];

const ELEMENT_COLUMN_PATTERN = /^(.+?) - (b'.+?') (b'.+?')$/;

const METADATA_COLUMNS = new Set([
    'Company', 'Location', 'WellID', 'Measurement ID', 'Line ID',
    'Depth From', 'Depth To', 'Hole ID',
]);

const MINERALS_METADATA_COLUMNS = new Set([
    'Depth From', 'Depth To', 'Hole ID',
]);

export function parseElementsFromColumns(xrfColumns: string[]) {
    const elements: any[] = [];
    let colorIndex = 0;

    // Two XRF column formats exist:
    //  - legacy emission lines:  "Iron - b'K' b'a'"  (shell/sub-shell encoded)
    //  - new unit format:        "Fe [%]"            (name with unit in brackets)
    // Detect the legacy format by the presence of any emission-line column; if
    // none match, treat every non-metadata column as an element (like minerals).
    const hasEmissionFormat = xrfColumns.some(col => ELEMENT_COLUMN_PATTERN.test(col));

    for (const col of xrfColumns) {
        if (METADATA_COLUMNS.has(col)) continue;

        if (hasEmissionFormat) {
            // Legacy: skip oxide percent columns, only keep emission lines.
            if (col.endsWith('%')) continue;
            const match = col.match(ELEMENT_COLUMN_PATTERN);
            if (!match) continue;
            const {name, unit} = parseNameAndUnit(match[1]);
            elements.push({
                id: elements.length + 1,
                name: name,
                unit: unit,
                shell: match[2],
                sub_shell: match[3],
                color: ELEMENT_COLORS[colorIndex % ELEMENT_COLORS.length],
                columnName: col,
                active: true,
                source: 'xrf',
            });
            colorIndex++;
        } else {
            // New format: each non-metadata column is an element, unit in brackets.
            const {name, unit} = parseNameAndUnit(col);
            elements.push({
                id: elements.length + 1,
                name: name,
                unit: unit,
                shell: '',
                sub_shell: '',
                color: ELEMENT_COLORS[colorIndex % ELEMENT_COLORS.length],
                columnName: col,
                active: true,
                source: 'xrf',
            });
            colorIndex++;
        }
    }

    return elements;
}

export function parseMineralColumnsFromData(mineralsData: any): any[] {
    if (!mineralsData?.data || mineralsData.data.length === 0) return [];

    const firstRow = mineralsData.data[0];
    const columns = Object.keys(firstRow).filter(col => !MINERALS_METADATA_COLUMNS.has(col));

    let colorIndex = 0;
    return columns.map((col, idx) => {
        const {name, unit} = parseNameAndUnit(col);
        return {
            id: idx + 1,
            name: name,
            unit: unit,
            shell: '',
            sub_shell: '',
            color: ELEMENT_COLORS[colorIndex++ % ELEMENT_COLORS.length],
            columnName: col,
            active: true,
            source: 'minerals',
        };
    });
}

function interpolateByDepth(dripos: any[], targetDepth: number) {
    if (!dripos || dripos.length === 0) return {x: 0, y: 0, z: 0};

    for (let i = 1; i < dripos.length; i++) {
        const prevD = dripos[i - 1].depth ?? 0;
        const currD = dripos[i].depth ?? 0;
        if (targetDepth >= prevD && targetDepth <= currD) {
            const span = currD - prevD;
            const t = span !== 0 ? (targetDepth - prevD) / span : 0;
            return {
                x: (dripos[i - 1].x ?? 0) + t * ((dripos[i].x ?? 0) - (dripos[i - 1].x ?? 0)),
                y: (dripos[i - 1].y ?? 0) + t * ((dripos[i].y ?? 0) - (dripos[i - 1].y ?? 0)),
                z: (dripos[i - 1].z ?? 0) + t * ((dripos[i].z ?? 0) - (dripos[i - 1].z ?? 0)),
            };
        }
    }

    const first = dripos[0];
    const last = dripos[dripos.length - 1];
    if (targetDepth <= (first.depth ?? 0)) return {x: first.x, y: first.y, z: first.z};
    return {x: last.x, y: last.y, z: last.z};
}

export function transformMineralsToMeasurements(
    mineralsData: any,
    elements: any[],
    dripos: any[],
    drillhole: any,
    offsetCoords3857?: number[],
): { dri_mea: any[], greatest_amount_value: number } {
    if (!mineralsData || !mineralsData.data || mineralsData.data.length === 0) return {dri_mea: [], greatest_amount_value: 0};

    const offsetX = offsetCoords3857 ? offsetCoords3857[0] : 0;
    const offsetY = offsetCoords3857 ? offsetCoords3857[1] : 0;

    let globalMaxAmount = 0;

    const mea_xrfres = mineralsData.data.map((row: any, rowIndex: number) => {
        const depthFrom = row['Depth From'] || 0;
        const depthTo = row['Depth To'] || 0;

        const spe_speres: any[] = [];
        let greatestAmount: any = null;

        for (const element of elements) {
            const value = row[element.columnName];
            if (value == null) continue;

            const entry = {
                element_id: element.id,
                value: value,
            };
            spe_speres.push(entry);

            if (!greatestAmount || value > greatestAmount.value) {
                greatestAmount = {...entry};
            }
        }

        if (greatestAmount && greatestAmount.value > globalMaxAmount) {
            globalMaxAmount = greatestAmount.value;
        }

        const interp1 = interpolateByDepth(dripos, depthFrom);
        const interp2 = interpolateByDepth(dripos, depthTo);

        return {
            id: `minerals_xrf_${rowIndex}`,
            index: rowIndex,
            start_position: depthFrom,
            stop_position: depthTo,
            measurement_id: 'minerals',
            xrfres_spe: [{
                id: rowIndex,
                index: 0,
                xrf_result_id: `minerals_xrf_${rowIndex}`,
                spe_speres: spe_speres,
                position: -depthFrom,
                greatest_amount: greatestAmount,
                x1: interp1.x,
                y1: interp1.y,
                z1: interp1.z,
                x2: interp2.x,
                y2: interp2.y,
                z2: interp2.z,
            }],
            step: depthTo - depthFrom,
        };
    });

    const dri_mea = [{
        id: 'minerals',
        company: '',
        location: '',
        drillhole_id: drillhole?.hole_id || '',
        latitude: drillhole?.collar?.LATITUDE,
        longitude: drillhole?.collar?.LONGITUDE,
        elevation: drillhole?.collar?.['RELATIVE LEVEL'],
        mea_xrfres: mea_xrfres,
    }];

    return {dri_mea, greatest_amount_value: globalMaxAmount};
}

export function buildDrillPath(
    collar: any,
    surfaceCoords3857: number[],
    offsetCoords3857?: number[],
) {
    if (!collar) return [];

    const totalDepth = collar.TOTALDEPTH || 100;
    const azimuthDeg = collar.AZIMUTH || 0;
    const dipDeg = collar.DIP || -90;
    const elevation = collar['RELATIVE LEVEL'] || 0;

    const azimuthRad = (azimuthDeg * Math.PI) / 180;
    const dipRad = (Math.abs(dipDeg) * Math.PI) / 180;

    const offsetX = offsetCoords3857 ? offsetCoords3857[0] : 0;
    const offsetY = offsetCoords3857 ? offsetCoords3857[1] : 0;

    const steps = Math.max(2, Math.ceil(totalDepth / 25));
    const depthStep = totalDepth / (steps - 1);
    const dripos: any[] = [];

    const baseLat = collar.LATITUDE ?? 0;
    const baseLon = collar.LONGITUDE ?? 0;

    const latRad = (baseLat * Math.PI) / 180;
    const metersPerDegLat = 111320;
    const metersPerDegLon = 111320 * Math.cos(latRad);

    for (let i = 0; i < steps; i++) {
        const depth = i * depthStep;
        const horizontalDist = depth * Math.cos(dipRad);
        const verticalDist = depth * Math.sin(dipRad);

        const dLat = (horizontalDist * Math.cos(azimuthRad)) / metersPerDegLat;
        const dLon = (horizontalDist * Math.sin(azimuthRad)) / metersPerDegLon;

        const lon = baseLon + dLon;
        const lat = baseLat + dLat;

        const coords3857 = proj4('EPSG:4326', 'EPSG:3857', [lon, lat]);

        const z = -verticalDist;

        dripos.push({
            drillhole_id: collar.hole_id,
            depth: depth,
            azimuth: azimuthDeg,
            deviation: dipDeg,
            longitude: lon,
            latitude: lat,
            x: coords3857[0] - offsetX,
            y: coords3857[1] - offsetY,
            z: z,
            height_label: z,
        });
    }

    return dripos;
}

export function transformXrfToMeasurements(
    xrfData: any,
    elements: any[],
    dripos: any[],
    drillhole: any,
    offsetCoords3857?: number[],
): {dri_mea: any[], greatest_amount_value: number} {
    if (!xrfData || !xrfData.data || xrfData.data.length === 0) return {dri_mea: [], greatest_amount_value: 0};

    const offsetX = offsetCoords3857 ? offsetCoords3857[0] : 0;
    const offsetY = offsetCoords3857 ? offsetCoords3857[1] : 0;

    const grouped: Record<string, any[]> = {};
    for (const row of xrfData.data) {
        const meaId = row['Measurement ID'] || 'default';
        if (!grouped[meaId]) grouped[meaId] = [];
        grouped[meaId].push(row);
    }

    let globalMaxAmount = 0;

    const dri_mea = Object.entries(grouped).map(([meaId, rows]) => {
        const mea_xrfres = rows.map((row: any, rowIndex: number) => {
            const depthFrom = row['Depth From'] || 0;
            const depthTo = row['Depth To'] || 0;

            const spe_speres: any[] = [];
            let greatestAmount: any = null;

            for (const element of elements) {
                const value = row[element.columnName];
                if (value == null) continue;

                const entry = {
                    element_id: element.id,
                    value: value,
                };
                spe_speres.push(entry);

                if (!greatestAmount || value > greatestAmount.value) {
                    greatestAmount = {...entry};
                }
            }

            if (greatestAmount && greatestAmount.value > globalMaxAmount) {
                globalMaxAmount = greatestAmount.value;
            }

            const interp1 = interpolateByDepth(dripos, depthFrom);
            const interp2 = interpolateByDepth(dripos, depthTo);

            return {
                id: `${meaId}_xrf_${rowIndex}`,
                index: rowIndex,
                start_position: depthFrom,
                stop_position: depthTo,
                measurement_id: meaId,
                xrfres_spe: [{
                    id: rowIndex,
                    index: 0,
                    xrf_result_id: `${meaId}_xrf_${rowIndex}`,
                    spe_speres: spe_speres,
                    position: -depthFrom,
                    greatest_amount: greatestAmount,
                    x1: interp1.x,
                    y1: interp1.y,
                    z1: interp1.z,
                    x2: interp2.x,
                    y2: interp2.y,
                    z2: interp2.z,
                }],
                step: depthTo - depthFrom,
            };
        });

        return {
            id: meaId,
            company: rows[0]?.Company || '',
            location: rows[0]?.Location || '',
            drillhole_id: drillhole?.hole_id || '',
            latitude: drillhole?.collar?.LATITUDE,
            longitude: drillhole?.collar?.LONGITUDE,
            elevation: drillhole?.collar?.['RELATIVE LEVEL'],
            mea_xrfres: mea_xrfres,
        };
    });

    return {dri_mea, greatest_amount_value: globalMaxAmount};
}


export function transformDrillhole(
    drillhole: any,
    info: any,
    xrfData: any,
    minerals: any,
    xrfElements: any[],
    mineralsElements: any[],
    offsetCoords3857?: number[],
) {
    const collar = drillhole.collar;

    const hasCoords = collar && collar.LONGITUDE != null && collar.LATITUDE != null;
    const surfaceCoords3857 = hasCoords
        ? proj4('EPSG:4326', 'EPSG:3857', [collar.LONGITUDE, collar.LATITUDE])
        : [0, 0];

    const dri_dripos = buildDrillPath(collar, surfaceCoords3857, offsetCoords3857);

    const {dri_mea, greatest_amount_value} = transformXrfToMeasurements(
        xrfData, xrfElements, dri_dripos, drillhole, offsetCoords3857,
    );

    const {dri_mea: dri_mea_minerals, greatest_amount_value: greatest_amount_value_minerals} =
        transformMineralsToMeasurements(
            minerals, mineralsElements, dri_dripos, drillhole, offsetCoords3857,
        );

    return {
        id: drillhole.hole_id,
        hole_id: drillhole.hole_id,
        latitude: collar?.LATITUDE,
        longitude: collar?.LONGITUDE,
        elevation: collar?.['RELATIVE LEVEL'],
        dri_dripos: dri_dripos,
        dri_mea: dri_mea,
        greatest_amount_value: greatest_amount_value,
        dri_mea_minerals: dri_mea_minerals,
        greatest_amount_value_minerals: greatest_amount_value_minerals,
        minerals: minerals,
        info: info,
    };
}
